import React, { useState } from 'react';

import type { CompanyInfoDTO } from '../../services/apiClient';

import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import handleChange from '../../utils/formUtils/handleChange';

interface CompanyInfoFormProps {
  initialValues: CompanyInfoDTO;
  onSubmit: (data: CompanyInfoDTO) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const FORM_SECTIONS = {
  companyDetails: {
    id: 'company-details',
    title: 'Company Details',
  },
  bankingInfo: {
    id: 'banking-info',
    title: 'Banking Information',
  },
  additionalInfo: {
    id: 'additional-info',
    title: 'Additional Information',
  },
} as const;

const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<CompanyInfoDTO>(initialValues);

  const handleSave = () => {
    onSubmit(formData);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <FormSection
        id={FORM_SECTIONS.companyDetails.id}
        title={FORM_SECTIONS.companyDetails.title}
        columns={2}
      >
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter name"
        />
        <FormField
          id="email"
          label="Email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter email"
        />
        <FormField
          id="phone"
          label="Phone"
          type="text"
          value={formData.phone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter phone number"
        />
        <FormField
          id="address"
          label="Address"
          type="text"
          value={formData.address || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter address"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.bankingInfo.id}
        title={FORM_SECTIONS.bankingInfo.title}
        columns={2}
      >
        <FormField
          id="bank"
          label="Bank"
          type="text"
          value={formData.bank || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter bank"
        />
        <FormField
          id="accounttype"
          label="Account Type"
          type="text"
          value={formData.accountType || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter account type"
        />
        <FormField
          id="branchcode"
          label="Branch Code"
          type="text"
          value={formData.branchCode || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter branch code"
        />
        <FormField
          id="accountnumber"
          label="Account Number"
          type="text"
          value={formData.accountNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter account number"
        />
        <FormField
          id="swiftcode"
          label="Swift Code"
          type="text"
          value={formData.swiftCode || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter swift code"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.additionalInfo.id}
        title={FORM_SECTIONS.additionalInfo.title}
      >
        <FormField
          id="notes"
          label="Notes"
          type="textarea"
          value={formData.notes || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter notes"
        />
      </FormSection>
    </Form>
  );
};

export default CompanyInfoForm;
